import * as _ from 'lodash';

import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FcpBadgeColor } from '../fcp-badge';

@Component({
    selector: 'fcp-dropdown',
    templateUrl: './fcp-dropdown.component.html',
    styleUrls: ['./fcp-dropdown.component.less'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FcpDropdownComponent
        }
    ],
    standalone: false
})
export class FcpDropdownComponent implements OnInit, ControlValueAccessor {
    /*
        TODO: Modify this component to be only responsible for the selection of component variant.
        All other logic should be moved to variant's classes and templates.
    */

    public isOpen = false;
    public selected: SelectedFcpDropdownItem | Array<SelectedFcpDropdownItem> = null;
    public badgeColor: FcpBadgeColor = FcpBadgeColor.Promotion;
    public badgeInversed = false;
    private _searchQuery = '';
    get searchQuery() {
        if (!this.hasSelection()) {
            return this._searchQuery;
        }

        return this.isMulti
            ? `${this.title} (${(this.selected as Array<SelectedFcpDropdownItem>).length})`
            : (this.selected as SelectedFcpDropdownItem).label;
    }

    @Input() icon?: string | Array<string>;
    @Input() isCustomIcon = false;
    @Input() title: string;
    @Input() e2eHook: string;
    @Input() items: Array<FcpDropdownItem> = [];
    @Input() isMulti = false;
    @Input() isMultiLine = false;
    @Input() compact = false;
    @Input() bordered = false;
    @Input() disabled = false;
    @Input() autoComplete = false;
    @Output() searchQueryChange = new EventEmitter<string>();
    @Output() close = new EventEmitter<FcpDropdownItem | FcpDropdownItem[]>();

    @ViewChild('container') container: ElementRef<HTMLDivElement>;

    ngOnInit(): void {
        this.resetSelection();
    }

    public onChange(item: SelectedFcpDropdownItem | Array<SelectedFcpDropdownItem>) {
        this.selected = item;
    }

    public onTouched(): void {}

    public touched = false;

    writeValue(value: SelectedFcpDropdownItem | Array<SelectedFcpDropdownItem>) {
        this.selected = value;
    }

    registerOnChange(onChange: any): void {
        this.onChange = (value) => {
            this.selected = value;
            onChange(value);
        };
    }

    registerOnTouched(onTouched: any): void {
        this.onTouched = onTouched;
    }

    markAsTouched(): void {
        if (!this.touched) {
            this.touched = true;
            this.onTouched();
        }
    }

    get placeholder() {
        if (!this.hasSelection()) {
            return this.title;
        }

        if (Array.isArray(this.selected)) {
            return `${this.title} (${(this.selected as Array<SelectedFcpDropdownItem>).length})`;
        }

        return this.selected.label;
    }

    handleClick() {
        if (this.isOpen) this.handleClose();
        else this.isOpen = true;
    }

    handleSelect(selectedItem: SelectedFcpDropdownItem) {
        this.markAsTouched();
        if (_.isFunction(selectedItem.action)) selectedItem.action();

        if (this.isMulti) {
            const selectedIndex = _.findIndex(this.selected as Array<FcpDropdownItem>, { value: selectedItem.value });

            if (selectedIndex !== -1) {
                const newSelectedItems = [...(this.selected as Array<FcpDropdownItem>)];
                newSelectedItems.splice(selectedIndex, 1);
                this.onChange(newSelectedItems);
            } else {
                const newSelectedItems = [...(this.selected as Array<FcpDropdownItem>), selectedItem];
                this.onChange(newSelectedItems);
            }
        } else {
            this.onChange(selectedItem);
            this.handleClose();
        }
    }

    handleClose() {
        this.isOpen = false;
        this.close.emit(this.selected);
    }

    isSelected(selectedItem: FcpDropdownItem) {
        if (this.isMulti) {
            return _.findIndex(this.selected as Array<SelectedFcpDropdownItem>, { value: selectedItem.value }) !== -1;
        }

        return (this.selected as SelectedFcpDropdownItem)?.value === selectedItem.value;
    }

    asFcpDropdownItem(item: any): FcpDropdownItem {
        return item as FcpDropdownItem;
    }

    @HostListener('document:click', ['$event.target'])
    clickListener(target: HTMLElement) {
        if (!this.isOpen) {
            return;
        }

        if (!this.container.nativeElement.contains(target) || target === this.container.nativeElement) {
            this.handleClose();
        }
    }

    hasSelection(): boolean {
        return this.isMulti
            ? Boolean((this.selected as Array<SelectedFcpDropdownItem>)?.length)
            : Boolean(this.selected);
    }

    handleSearchQueryChange(searchQuery: string) {
        if (searchQuery.length === 0) {
            this.resetSelection();
            this.items = [];
        }

        this.searchQueryChange.emit(searchQuery);
    }

    getSelectionInfo(value) {
        if (!this.isMulti) return 0;
        const selectedIndex = _.findIndex(this.selected as Array<SelectedFcpDropdownItem>, { value });
        return this.selected[selectedIndex].selectionInfo;
    }

    private resetSelection() {
        if (this.isMulti) {
            this.selected = [];
        } else {
            this.selected = null;
        }
    }
}

export interface FcpDropdownItem {
    description?: string;
    label: string;
    value: any;
    icon?: string;
    iconStyle?: Partial<CSSStyleDeclaration>;
    isCustomIcon?: boolean;
    action?: () => void;
    colored?: boolean;
    badgeText?: string;
    badgeColor?: FcpBadgeColor;
    badgeInversed?: boolean;
}

export interface SelectedFcpDropdownItem extends FcpDropdownItem {
    selectionInfo?: string;
}
